// 클레임 사유코드 관리(구성안 화면⑦) — 도구줄(전체/일반사고/택배사고 탭 · 코드/사유명 검색 · 사용 중지 포함 · 순서 변경 · 엑셀 다운로드 · 코드 추가)
// · 왼쪽 7열 표(사고구분 묶음 배지 · 코드 · 상세사유 · 보상기준 연결 유형 · 범위 선택 · 필수 증빙 · 상태) + 안내 문구
// · 오른쪽 코드 상세(포털 표시명 · 필수 증빙 체크 · 범위 선택 체크 · 접수 안내 문구 · AI 분류 키워드 칩 · 코드별 변경이력 · 사용 중지 · 저장
//   · 2.0 상세사유 연결 · 1.0 상세사유 연결) · 아래 «WMS 상세사유 분류» 카드(claims/ClaimWmsReasonPanel — 1.0 이름·분류·추가 · 2.0 분류).
// 서버 routers/claims.py · 응답 모양은 services/claim_master.load_reasons.
// 🔴 GET 의 can_edit(= claim-admin) 가 거짓이면 입력은 disabled 이고 저장·코드 추가·순서 변경·연결 저장·동기화·사용 중지 단추는 그리지 않는다
//    (엑셀 다운로드·변경이력은 보기 권한이면 있다).
// 🔴 저장은 바뀐 칸만 보낸다(ai_keywords 는 목록으로 보내고 비교는 콤마로 합친 글자로) · 성공하면 GET 을 다시 읽는다
//    · 실패는 detail.message 를 칸 옆(role=alert)과 toast 로.
// 🔴 삭제 단추는 없다(서버에 DELETE 경로가 없다 — 사용 중지만). 코드는 만든 뒤 바꿀 수 없다(읽기 전용).
// 🔴 필수 증빙은 장수·설명·필수 체크·범위 선택 체크로만 다룬다 — 사진 종류별 체크리스트·자동 검증은 2단계 등록 화면에서 정한다.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IconClipboardList } from '@tabler/icons-react';
import AppShell from '../components/AppShell';
import { Page, PageHeader, PillTabs, Button, SearchInput } from '../components/ui';
import { claimsAPI } from '../api/client';
import { toast } from '../components/AppDialog';
import { fmtKstDateTime } from '../utils/kstTime';
import { blobDownload } from '../utils/meetingUtil';
import { COLORS, badge, card, input } from '../utils/uiStyles';
import ClaimHistoryPanel from './claims/ClaimHistoryPanel';
import ClaimWmsReasonPanel from './claims/ClaimWmsReasonPanel';
import {
  ACCIDENT_DETAIL_LABELS, ACCIDENT_TABS, FALLBACK_LABELS, GENERAL_RULE_GROUPS, V2_DETAIL_GROUP, asText, changedFields,
  errorText, labelOf, linkLabel, numOrNull, statusOf, textOrNull,
} from './claims/claimLabels';

const ID_BASE = 'claim-reasons';
const fieldBox = { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: COLORS.textSub, minWidth: '150px' };
const checkBox = { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: COLORS.text };
const formGrid = { display: 'flex', flexWrap: 'wrap', gap: '10px 14px', alignItems: 'flex-end' };
const errText = { color: COLORS.dangerText, fontSize: '12px', marginTop: '8px' };
const subTitle = { margin: '18px 0 8px', fontSize: '13px', fontWeight: 600, color: COLORS.text };
const th = { textAlign: 'left', padding: '7px 8px', fontSize: '12px', fontWeight: 500, color: COLORS.textSub, borderBottom: `1px solid ${COLORS.border}`, whiteSpace: 'nowrap' };
const td = { padding: '8px', fontSize: '13px', color: COLORS.text, borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top' };
const codeBtn = { border: 0, background: 'transparent', padding: 0, cursor: 'pointer', color: COLORS.primaryDark, fontWeight: 500, fontSize: '13px' };
const chip = { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 8px', borderRadius: '20px', fontSize: '12px', color: COLORS.text };
const chipX = { border: 0, background: 'transparent', cursor: 'pointer', color: COLORS.textSub, padding: 0, fontSize: '13px', lineHeight: 1 };
const portalBadge = <span style={badge('blue')}>포털 표시</span>;
const NO_CHANGE = '바뀐 칸이 없습니다.';
const FAULTS = ['center', 'courier', 'shipper'];
const MAX_OPTIONS = 30;
const TAB_ITEMS = [{ key: 'all', label: '전체' }, ...ACCIDENT_TABS];
const TABLE_COLUMNS = ['사고구분', '코드', '상세사유', '보상기준 연결 유형', '범위 선택', '필수 증빙', '상태'];
const TABLE_NOTE = "'건별 판정' 사유는 보상기준 자동계산 없이 승인 단계에서 담당자가 금액 확정 · 코드 변경 시 과거 데이터는 기존 코드로 보존";
const ACCIDENT_TONE = { general: 'blue', courier: 'orange' };
const ACCIDENT_SHORT = Object.fromEntries(ACCIDENT_TABS.map((t) => [t.key, t.label]));   // 표 배지 — 일반사고 · 택배사고
const EXPORT_FALLBACK = '클레임_사유코드.xlsx';
const DASH = '–';

function NeedsCheck() {
  return <span data-testid="claim-needs-check" style={badge('warn')}>확인 필요</span>;
}
function Field({ label, children }) {
  return <label style={fieldBox}><span>{label}</span>{children}</label>;
}
// 라벨 옆에 «포털 표시» 배지 — 배지는 label 밖에 둔다(칸 이름에 섞이지 않게).
function PortalField({ id, label, children }) {
  return (
    <div style={fieldBox}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><label htmlFor={id}>{label}</label>{portalBadge}</div>
      {children}
    </div>
  );
}
const withReason = (body, reason) => {
  const r = textOrNull(reason);
  return r ? { ...body, reason: r } : body;
};
function fileNameOf(response, fallback) {
  const disposition = (response && response.headers && response.headers['content-disposition']) || '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (!encoded) return fallback;
  try { return decodeURIComponent(encoded[1].trim()); } catch { return fallback; }
}

// 비교하는 칸(ai_keywords 는 따로 — 목록이라 콤마로 합쳐 비교한다).
const REASON_FIELDS = [
  'accident_type', 'name', 'comp_rule_group', 'comp_scope', 'default_fault', 'evidence_min', 'evidence_guide',
  'portal_name', 'portal_guide', 'note', 'needs_check',
];
const reasonBase = (it) => Object.fromEntries(REASON_FIELDS.map((k) => [k, k === 'needs_check' ? !!it[k] : (it[k] ?? null)]));
const keywordsOf = (it) => (it && Array.isArray(it.ai_keywords) ? it.ai_keywords : []);
const v1LinkCodes = (it) => (it && Array.isArray(it.v1_links) ? it.v1_links.map((l) => l.v1_code) : []);
const isGeneralGroup = (group) => GENERAL_RULE_GROUPS.includes(group);
// 사고구분에 맞는 보상기준 묶음 — 일반사고 = 누락·오배송 · 택배사고 = 그 밖.
const groupsFor = (ruleGroups, accidentType) => ruleGroups.filter((g) => (accidentType === 'general') === isGeneralGroup(g.code));
const draftOf = (it, tab) => ({
  code: it ? it.code : '',
  accident_type: it ? it.accident_type : (tab === 'courier' ? 'courier' : 'general'),
  name: asText(it && it.name),
  comp_rule_group: asText(it && it.comp_rule_group),
  comp_scope: asText(it && it.comp_scope),
  default_fault: asText(it && it.default_fault),
  evidence_min: it ? asText(it.evidence_min) : '0',
  evidence_guide: asText(it && it.evidence_guide),
  portal_name: asText(it && it.portal_name),
  portal_guide: asText(it && it.portal_guide),
  ai_keywords: [...keywordsOf(it)],
  note: asText(it && it.note),
  needs_check: !!(it && it.needs_check),
  reason: '',
});
// 사고구분 순서(일반사고 → 택배사고)로 묶는다 — 묶음 안은 받은 순서(sort_order) 그대로.
const groupByAccident = (list) => [
  ...ACCIDENT_TABS.flatMap((t) => list.filter((it) => it.accident_type === t.key)),
  ...list.filter((it) => !ACCIDENT_TABS.some((t) => t.key === it.accident_type)),
];
const evidenceText = (it) => {
  const n = Number(it.evidence_min) || 0;
  return [it.evidence_guide, n > 0 ? `${n}장` : null].filter(Boolean).join(' · ') || DASH;
};

// ── 상세 ──────────────────────────────────────────────────────────────────
function ReasonDetail({ item, tab, data, canEdit, runSave, onCreated, onClose, onOpenHistory }) {
  const creating = !item;
  const ruleGroups = Array.isArray(data.rule_groups) ? data.rule_groups : [];
  const noGroupLabel = data.no_rule_group_label || '건별 판정';
  const [draft, setDraft] = useState(() => draftOf(item, tab));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [links, setLinks] = useState(() => (item && Array.isArray(item.v2_links) ? item.v2_links.map((l) => l.v2_detail_cd) : []));
  const [query, setQuery] = useState('');
  const [manual, setManual] = useState('');
  const [linkReason, setLinkReason] = useState('');
  const [linkError, setLinkError] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [v1Links, setV1Links] = useState(() => v1LinkCodes(item));
  const [v1Reason, setV1Reason] = useState('');
  const [v1Error, setV1Error] = useState('');
  const [v1Busy, setV1Busy] = useState(false);
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));

  // 코드별 변경이력 — 그 코드의 사유 행과 2.0 연결 이력만(서버 key 필터).
  const code = item ? item.code : '';
  const historyLoad = useCallback((p) => claimsAPI.reasonsHistory({ ...p, key: code }), [code]);

  // 2.0 상세사유 코드표(CLAIM_RESN_DETAIL_CD) — 코드 → 이름. 코드표에 없는 연결은 «코드표 없음».
  const detailCodes = useMemo(
    () => (Array.isArray(data.v2_codes) ? data.v2_codes : []).filter((c) => c.code_group === V2_DETAIL_GROUP),
    [data.v2_codes],
  );
  const nameByCode = useMemo(() => new Map(detailCodes.map((c) => [c.code, c.name])), [detailCodes]);
  const linkNames = useMemo(
    () => new Map((item && Array.isArray(item.v2_links) ? item.v2_links : []).map((l) => [l.v2_detail_cd, l.name])),
    [item],
  );
  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    return detailCodes
      .filter((c) => !q || String(c.code).toLowerCase().includes(q) || String(c.name || '').toLowerCase().includes(q))
      .slice(0, MAX_OPTIONS);
  }, [detailCodes, query]);
  const original = item && Array.isArray(item.v2_links) ? item.v2_links.map((l) => l.v2_detail_cd) : [];
  const linksDirty = links.length !== original.length || links.some((c, i) => c !== original[i]);

  // 1.0 상세사유 코드표(게이트웨이 정본) — 코드 → 이름. 선택지는 사용 중인 코드 + 이미 연결된 코드. 코드표에 없는 연결은 «코드표 없음».
  const v1Codes = useMemo(() => (Array.isArray(data.v1_codes) ? data.v1_codes : []), [data.v1_codes]);
  const v1NameByCode = useMemo(() => new Map(v1Codes.map((c) => [c.code, c.name])), [v1Codes]);
  const v1LinkNames = useMemo(
    () => new Map((item && Array.isArray(item.v1_links) ? item.v1_links : []).map((l) => [l.v1_code, l.name])),
    [item],
  );
  const v1Options = v1Codes.filter((c) => c.active || v1Links.includes(c.code));
  const v1Original = v1LinkCodes(item);
  const v1Dirty = v1Links.length !== v1Original.length || v1Links.some((c, i) => c !== v1Original[i]);

  const groupLabel = (g) => {
    const found = ruleGroups.find((x) => x.code === g);
    return found ? found.label : labelOf('rule_group', g, FALLBACK_LABELS);
  };
  const groupOptions = groupsFor(ruleGroups, draft.accident_type);
  if (draft.comp_rule_group && !groupOptions.some((g) => g.code === draft.comp_rule_group)) {
    groupOptions.push({ code: draft.comp_rule_group, label: groupLabel(draft.comp_rule_group) });
  }
  const general = isGeneralGroup(draft.comp_rule_group);
  const evidenceOn = Number(draft.evidence_min) > 0;

  const onAccident = (value) => setDraft((d) => {
    const fits = !d.comp_rule_group || groupsFor(ruleGroups, value).some((g) => g.code === d.comp_rule_group);
    return fits ? { ...d, accident_type: value } : { ...d, accident_type: value, comp_rule_group: '', comp_scope: '' };
  });
  // 일반 묶음을 고르면 범위는 지금 값(없으면 all = 범위 선택 해제) · 그 밖에는 범위를 비운다.
  const onGroup = (value) => setDraft((d) => ({
    ...d, comp_rule_group: value, comp_scope: isGeneralGroup(value) ? (d.comp_scope || 'all') : '',
  }));
  const addKeywords = () => {
    const words = keywordInput.split(',').map((w) => w.trim()).filter(Boolean);
    if (!words.length) return;
    setDraft((d) => {
      const list = [...d.ai_keywords];
      words.forEach((w) => { if (!list.includes(w)) list.push(w); });
      return { ...d, ai_keywords: list };
    });
    setKeywordInput('');
  };
  const removeKeyword = (word) => setDraft((d) => ({ ...d, ai_keywords: d.ai_keywords.filter((w) => w !== word) }));

  const onSave = async () => {
    const next = {
      accident_type: draft.accident_type,
      name: draft.name.trim(),
      comp_rule_group: draft.comp_rule_group || null,
      comp_scope: general ? (draft.comp_scope || null) : null,
      default_fault: draft.default_fault || null,
      evidence_min: numOrNull(draft.evidence_min),
      evidence_guide: textOrNull(draft.evidence_guide),
      portal_name: textOrNull(draft.portal_name),
      portal_guide: textOrNull(draft.portal_guide),
      note: textOrNull(draft.note),
      needs_check: draft.needs_check,
    };
    const keywords = draft.ai_keywords;
    let call;
    if (creating) {
      const body = { code: draft.code.trim() };
      Object.entries(next).forEach(([k, v]) => { if (v !== null && v !== '') body[k] = v; });
      if (keywords.length) body.ai_keywords = keywords;
      call = () => claimsAPI.createReason(withReason(body, draft.reason));
    } else {
      const body = changedFields(reasonBase(item), next);
      if (keywords.join(',') !== keywordsOf(item).join(',')) body.ai_keywords = keywords;
      if (!Object.keys(body).length) { setError(NO_CHANGE); return; }
      call = () => claimsAPI.patchReason(item.id, withReason(body, draft.reason));
    }
    setBusy(true);
    setError('');
    const res = await runSave(call, creating ? '추가했습니다' : '저장했습니다');
    setBusy(false);
    if (res.error) setError(res.error);
    else if (creating && res.data && res.data.item) onCreated(res.data.item.id);
  };

  const onToggleActive = async () => {
    setError('');
    const res = await runSave(() => claimsAPI.patchReason(item.id, { active: !item.active }),
      item.active ? '사용 중지했습니다' : '다시 사용합니다');
    if (res.error) setError(res.error);
  };

  const onSaveLinks = async () => {
    setLinkBusy(true);
    setLinkError('');
    const res = await runSave(() => claimsAPI.putReasonLinks(item.id, withReason({ codes: links }, linkReason)), '연결을 저장했습니다');
    setLinkBusy(false);
    if (res.error) setLinkError(res.error);
  };

  const onSaveV1Links = async () => {
    setV1Busy(true);
    setV1Error('');
    const res = await runSave(() => claimsAPI.putReasonV1Links(item.id, withReason({ codes: v1Links }, v1Reason)), '1.0 연결을 저장했습니다');
    setV1Busy(false);
    if (res.error) setV1Error(res.error);
  };
  const toggleV1 = (c) => setV1Links((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const toggleLink = (c) => setLinks((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  const addManual = () => {
    const c = manual.trim();
    if (!c) return;
    setLinks((prev) => (prev.includes(c) ? prev : [...prev, c]));
    setManual('');
  };

  return (
    <div data-testid="claim-reason-detail">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: COLORS.text }}>
          {creating ? '코드 추가' : `코드 상세 · ${item.code} ${item.name}`}
        </h2>
        {item && item.needs_check && <NeedsCheck />}
        {item && !item.active && <span style={badge('gray')}>사용 중지</span>}
        <Button variant="quiet" size="sm" style={{ marginLeft: 'auto' }} onClick={onClose}>닫기</Button>
      </div>

      <div style={formGrid}>
        <Field label="코드">
          <input type="text" value={draft.code} readOnly={!creating}
            placeholder={creating ? '영문 대문자로 시작 · 2~10자' : undefined}
            onChange={(e) => set('code')(e.target.value)} style={{ ...input, width: '120px' }} />
        </Field>
        <Field label="사고구분">
          <select value={draft.accident_type} disabled={!canEdit} onChange={(e) => onAccident(e.target.value)} style={{ ...input, width: '200px' }}>
            {ACCIDENT_TABS.map((t) => <option key={t.key} value={t.key}>{ACCIDENT_DETAIL_LABELS[t.key] || t.label}</option>)}
          </select>
        </Field>
        <Field label="상세사유">
          <input type="text" value={draft.name} disabled={!canEdit} onChange={(e) => set('name')(e.target.value)} style={{ ...input, width: '200px' }} />
        </Field>
        <Field label="보상기준 연결 유형">
          <select value={draft.comp_rule_group} disabled={!canEdit} onChange={(e) => onGroup(e.target.value)} style={{ ...input, width: '220px' }}>
            <option value="">{noGroupLabel}</option>
            {groupOptions.map((g) => <option key={g.code} value={g.code}>{`${g.label} (보상기준 마스터)`}</option>)}
          </select>
        </Field>
        <PortalField id={`${ID_BASE}-portal-name`} label="고객사 포털 표시명">
          <input id={`${ID_BASE}-portal-name`} type="text" value={draft.portal_name} maxLength={50} disabled={!canEdit}
            onChange={(e) => set('portal_name')(e.target.value)} style={{ ...input, width: '280px' }} />
        </PortalField>
      </div>

      <fieldset style={{ border: `1px solid ${COLORS.borderSoft}`, borderRadius: '6px', padding: '8px 12px 12px', margin: '14px 0 0' }}>
        <legend style={{ fontSize: '12px', color: COLORS.textSub, padding: '0 4px' }}>필수 증빙</legend>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={checkBox}>
            <input type="checkbox" checked={evidenceOn} disabled={!canEdit}
              onChange={(e) => set('evidence_min')(e.target.checked ? (evidenceOn ? draft.evidence_min : '1') : '0')} />
            최소 증빙 장수 (부족하면 증빙 미비로 표시 · 등록은 가능)
          </label>
          <div style={formGrid}>
            <Field label="최소 장수">
              <input type="text" inputMode="numeric" value={draft.evidence_min} disabled={!canEdit} onChange={(e) => set('evidence_min')(e.target.value)} style={{ ...input, width: '90px' }} />
            </Field>
            <Field label="증빙 설명">
              <input type="text" value={draft.evidence_guide} maxLength={300} disabled={!canEdit} onChange={(e) => set('evidence_guide')(e.target.value)} style={{ ...input, width: '320px' }} />
            </Field>
          </div>
          <label style={checkBox}>
            <input type="checkbox" checked={general && draft.comp_scope === 'select'} disabled={!canEdit || !general}
              onChange={(e) => set('comp_scope')(e.target.checked ? 'select' : 'all')} />
            범위 선택 사용 (일부 / 전제품)
          </label>
          {!general && <span style={{ fontSize: '12px', color: COLORS.textMuted }}>범위 선택은 누락·오배송 묶음에서만 씁니다.</span>}
        </div>
      </fieldset>

      <div style={{ ...formGrid, marginTop: '14px' }}>
        <PortalField id={`${ID_BASE}-portal-guide`} label="접수 안내 문구">
          <textarea id={`${ID_BASE}-portal-guide`} value={draft.portal_guide} maxLength={500} rows={3} disabled={!canEdit}
            onChange={(e) => set('portal_guide')(e.target.value)} style={{ ...input, width: '420px', maxWidth: '100%', resize: 'vertical' }} />
        </PortalField>
      </div>

      <div style={{ marginTop: '14px' }}>
        <div style={{ fontSize: '12px', color: COLORS.textSub, marginBottom: '6px' }}>AI 분류 키워드</div>
        <div role="group" aria-label="AI 분류 키워드" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          {draft.ai_keywords.length === 0 && <span style={{ fontSize: '12px', color: COLORS.textMuted }}>키워드가 없습니다.</span>}
          {draft.ai_keywords.map((w) => (
            <span key={w} data-testid="claim-keyword-chip" style={{ ...chip, background: COLORS.primarySoft }}>
              {w}
              {canEdit && <button type="button" aria-label={`키워드 ${w} 빼기`} onClick={() => removeKeyword(w)} style={chipX}>×</button>}
            </span>
          ))}
          {canEdit && (
            <>
              <input type="text" aria-label="AI 분류 키워드 입력" value={keywordInput} placeholder="키워드"
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeywords(); } }}
                style={{ ...input, width: '140px' }} />
              <Button variant="secondary" size="sm" onClick={addKeywords}><span aria-hidden="true">+ </span>추가</Button>
            </>
          )}
        </div>
      </div>

      <div style={{ ...formGrid, marginTop: '14px' }}>
        <Field label="귀책 기본값">
          <select value={draft.default_fault} disabled={!canEdit} onChange={(e) => set('default_fault')(e.target.value)} style={{ ...input, width: '120px' }}>
            <option value="">—(없음)</option>
            {FAULTS.map((f) => <option key={f} value={f}>{labelOf('fault', f, FALLBACK_LABELS)}</option>)}
          </select>
        </Field>
        <Field label="메모">
          <textarea value={draft.note} disabled={!canEdit} rows={2} onChange={(e) => set('note')(e.target.value)} style={{ ...input, width: '320px', resize: 'vertical' }} />
        </Field>
        <label style={checkBox}>
          <input type="checkbox" checked={draft.needs_check} disabled={!canEdit} onChange={(e) => set('needs_check')(e.target.checked)} />확인 필요로 표시
        </label>
        {canEdit && (
          <Field label="변경 사유(선택)">
            <input type="text" value={draft.reason} maxLength={200} onChange={(e) => set('reason')(e.target.value)} style={{ ...input, width: '240px' }} />
          </Field>
        )}
      </div>
      {error && <div role="alert" style={errText}>{error}</div>}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginTop: '12px' }}>
        {!creating && (
          <Button variant="secondary" onClick={() => onOpenHistory(historyLoad, `${item.code} ${item.name}`, item.code)}>변경이력</Button>
        )}
        {canEdit && !creating && (
          <Button variant="secondary" onClick={onToggleActive}>{item.active ? '사용 중지' : '다시 사용'}</Button>
        )}
        {canEdit && (
          <Button onClick={onSave} disabled={busy} style={{ marginLeft: 'auto' }}>{busy ? '저장 중…' : '저장'}</Button>
        )}
      </div>

      {!creating && (
        <div data-testid="claim-v2-links">
          <h3 style={subTitle}>2.0 상세사유 연결</h3>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {links.length === 0 && <span style={{ fontSize: '12px', color: COLORS.textMuted }}>연결된 2.0 상세사유가 없습니다.</span>}
            {links.map((c) => {
              const inTable = nameByCode.has(c);
              const name = inTable ? nameByCode.get(c) : linkNames.get(c);
              return (
                <span key={c} data-testid="claim-v2-chip"
                  style={{ ...chip, background: inTable ? COLORS.primarySoft : COLORS.dangerSoft }}>
                  {name || c} <span style={{ color: COLORS.textMuted }}>{c}</span>
                  {!inTable && <span style={{ color: COLORS.dangerText }}>코드표 없음</span>}
                  {canEdit && (
                    <button type="button" aria-label={`${c} 빼기`} onClick={() => toggleLink(c)} style={chipX}>×</button>
                  )}
                </span>
              );
            })}
          </div>
          {canEdit && (
            <div style={{ marginTop: '10px' }}>
              <SearchInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="2.0 상세사유 검색(코드·이름)" width="320px" />
              <div role="group" aria-label="2.0 상세사유 선택" style={{ marginTop: '6px', maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {options.length === 0 && <span style={{ fontSize: '12px', color: COLORS.textMuted }}>맞는 코드가 없습니다.</span>}
                {options.map((c) => (
                  <label key={c.code} style={{ ...checkBox, fontSize: '12px' }}>
                    <input type="checkbox" checked={links.includes(c.code)} onChange={() => toggleLink(c.code)} />
                    {c.name} ({c.code}){!c.in_source && <span style={{ color: COLORS.textMuted }}> · WMS 에 없음</span>}
                  </label>
                ))}
              </div>
              <div style={{ ...formGrid, marginTop: '8px' }}>
                <Field label="직접 입력(쇼핑몰 수집분)">
                  <input type="text" value={manual} onChange={(e) => setManual(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addManual(); } }} style={{ ...input, width: '160px' }} />
                </Field>
                <Button variant="secondary" onClick={addManual}>코드 넣기</Button>
                <Field label="변경 사유(선택)">
                  <input type="text" value={linkReason} maxLength={200} onChange={(e) => setLinkReason(e.target.value)} style={{ ...input, width: '200px' }} />
                </Field>
                <Button onClick={onSaveLinks} disabled={linkBusy || !linksDirty}>{linkBusy ? '저장 중…' : '연결 저장'}</Button>
              </div>
            </div>
          )}
          {linkError && <div role="alert" style={errText}>{linkError}</div>}
        </div>
      )}

      {!creating && (
        <div data-testid="claim-v1-links">
          <h3 style={subTitle}>1.0 상세사유 연결</h3>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {v1Links.length === 0 && <span style={{ fontSize: '12px', color: COLORS.textMuted }}>연결된 1.0 상세사유가 없습니다.</span>}
            {v1Links.map((c) => {
              const inTable = v1NameByCode.has(c);
              const name = inTable ? v1NameByCode.get(c) : v1LinkNames.get(c);
              return (
                <span key={c} data-testid="claim-v1-chip"
                  style={{ ...chip, background: inTable ? COLORS.primarySoft : COLORS.dangerSoft }}>
                  {c} {name || ''}
                  {!inTable && <span style={{ color: COLORS.dangerText }}>코드표 없음</span>}
                  {canEdit && (
                    <button type="button" aria-label={`1.0 ${c} 빼기`} onClick={() => toggleV1(c)} style={chipX}>×</button>
                  )}
                </span>
              );
            })}
          </div>
          {canEdit && (
            <div style={{ marginTop: '10px' }}>
              <div role="group" aria-label="1.0 상세사유 선택" style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {v1Options.length === 0 && <span style={{ fontSize: '12px', color: COLORS.textMuted }}>1.0 상세사유 코드가 없습니다.</span>}
                {v1Options.map((c) => (
                  <label key={c.code} style={{ ...checkBox, fontSize: '12px' }}>
                    <input type="checkbox" checked={v1Links.includes(c.code)} onChange={() => toggleV1(c.code)} />
                    {`${c.code} ${c.name}`}{!c.active && <span style={{ color: COLORS.textMuted }}> · 사용 중지</span>}
                  </label>
                ))}
              </div>
              <div style={{ ...formGrid, marginTop: '8px' }}>
                <Field label="변경 사유(선택)">
                  <input type="text" value={v1Reason} maxLength={200} onChange={(e) => setV1Reason(e.target.value)} style={{ ...input, width: '200px' }} />
                </Field>
                <Button onClick={onSaveV1Links} disabled={v1Busy || !v1Dirty}>{v1Busy ? '저장 중…' : '연결 저장'}</Button>
              </div>
            </div>
          )}
          {v1Error && <div role="alert" style={errText}>{v1Error}</div>}
        </div>
      )}
    </div>
  );
}

// ── 화면 ──────────────────────────────────────────────────────────────────
export default function ClaimReasonsPage() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');   // loading | ready | forbidden | error
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [withInactive, setWithInactive] = useState(false);
  const [selected, setSelected] = useState(null);     // null | 'new' | 사유 id
  const [detailKey, setDetailKey] = useState(0);      // 다시 읽은 뒤 상세 입력을 새 값으로
  const [history, setHistory] = useState(null);       // null | {kind:'all'} | {kind:'code', code, load, subtitle}
  const [historyKey, setHistoryKey] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [ordering, setOrdering] = useState(null);     // null | 사유 id 전체의 새 순서(순서 변경 모드)
  const [orderBusy, setOrderBusy] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await claimsAPI.reasons();
      setData(r.data || {});
      setStatus('ready');
    } catch (e) {
      if (statusOf(e) === 403) { setStatus('forbidden'); return; }
      const msg = errorText(e, '사유코드를 불러오지 못했습니다');
      setLoadError(msg);
      setStatus((s) => (s === 'ready' ? s : 'error'));
      toast(msg, 'error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 저장 1회 — 성공하면 GET 을 다시 읽고 상세·이력 패널을 새로 그린다. 돌려주는 값 {error} | {data}.
  const runSave = useCallback(async (call, okText = '저장했습니다') => {
    let res;
    try {
      res = await call();
    } catch (e) {
      const msg = errorText(e);
      toast(msg, 'error');
      return { error: msg };
    }
    toast(okText);
    await load();
    setDetailKey((k) => k + 1);
    setHistoryKey((k) => k + 1);
    return { data: (res && res.data) || null };
  }, [load]);

  const onSync = async () => {
    setSyncing(true);
    try {
      const r = await claimsAPI.syncV2Reasons();
      setSyncResult(r.data || null);
      toast('2.0 사유코드를 동기화했습니다');
      await load();
      setDetailKey((k) => k + 1);
      setHistoryKey((k) => k + 1);
    } catch (e) {
      toast(errorText(e, '동기화에 실패했습니다'), 'error');
    } finally {
      setSyncing(false);
    }
  };

  const onExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const r = await claimsAPI.exportReasons();
      blobDownload(r.data, fileNameOf(r, EXPORT_FALLBACK));
    } catch (e) {
      toast(errorText(e, '엑셀을 내려받지 못했습니다'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const canEdit = !!(data && data.can_edit);
  const items = useMemo(() => (data && Array.isArray(data.items) ? data.items : []), [data]);
  const byId = useMemo(() => new Map(items.map((it) => [it.id, it])), [items]);
  const ruleLabel = useMemo(() => {
    const map = new Map((data && Array.isArray(data.rule_groups) ? data.rule_groups : []).map((g) => [g.code, g.label]));
    return (code) => (code ? (map.get(code) || labelOf('rule_group', code, FALLBACK_LABELS)) : null);
  }, [data]);

  const counted = items.filter((it) => withInactive || it.active);
  const counts = {
    all: counted.length,
    ...Object.fromEntries(ACCIDENT_TABS.map((t) => [t.key, counted.filter((it) => it.accident_type === t.key).length])),
  };
  const q = query.trim().toLowerCase();
  const matches = (it) => !q || [it.code, it.name, it.portal_name].some((v) => String(v || '').toLowerCase().includes(q));
  const rows = ordering
    ? ordering.map((id) => byId.get(id)).filter(Boolean)
    : groupByAccident(counted.filter((it) => (tab === 'all' || it.accident_type === tab) && matches(it)));
  const shownTab = ordering ? 'all' : tab;
  const current = typeof selected === 'number' ? byId.get(selected) || null : null;

  const changeTab = (key) => {
    if (ordering) return;   // 순서 변경 중에는 전체 탭에 머문다
    setTab(key);
    if (current && key !== 'all' && current.accident_type !== key) setSelected(null);
  };
  const selectRow = (id) => {
    setSelected(id);
    const it = byId.get(id);
    setHistory((h) => (h && h.kind === 'code' && (!it || h.code !== it.code) ? null : h));
  };
  const startCreate = () => {
    setSelected('new');
    setHistory((h) => (h && h.kind === 'code' ? null : h));
  };
  const openCodeHistory = (historyLoad, subtitle, code) => setHistory({ kind: 'code', code, load: historyLoad, subtitle });

  // 순서 변경 — 전체 탭 · 검색 비움 · 사용 중지 포함으로 바꾸고, 같은 사고구분 안에서만 ▲▼ 로 옮긴다.
  const startOrdering = () => {
    setTab('all');
    setQuery('');
    setWithInactive(true);
    setOrderError('');
    setOrdering(groupByAccident(items).map((it) => it.id));
  };
  const cancelOrdering = () => {
    setOrdering(null);
    setOrderError('');
  };
  const move = (id, step) => setOrdering((prev) => {
    if (!prev) return prev;
    const list = [...prev];
    const i = list.indexOf(id);
    const j = i + step;
    if (i < 0 || j < 0 || j >= list.length) return prev;
    const a = byId.get(list[i]);
    const b = byId.get(list[j]);
    if (!a || !b || a.accident_type !== b.accident_type) return prev;
    [list[i], list[j]] = [list[j], list[i]];
    return list;
  });
  const saveOrder = async () => {
    setOrderBusy(true);
    setOrderError('');
    const res = await runSave(() => claimsAPI.putReasonOrder({ ids: ordering }), '순서를 저장했습니다');
    setOrderBusy(false);
    if (res.error) setOrderError(res.error);
    else setOrdering(null);
  };

  const columnCount = TABLE_COLUMNS.length + (ordering ? 1 : 0);

  return (
    <AppShell app="claim-reasons">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', display: 'flex', overflow: 'hidden', background: `var(--gw-bg, ${COLORS.bg})` }}>
        <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
          <Page>
            <PageHeader icon={IconClipboardList} tone="red" title="사유코드 관리"
              subtitle={status === 'ready' ? `2.0 사유코드 마지막 동기화: ${data.v2_synced_at ? fmtKstDateTime(data.v2_synced_at) : '없음'}` : null}
              actions={(
                <>
                  {status === 'ready' && !canEdit && <span data-testid="claim-readonly" style={badge('gray')}>보기 전용</span>}
                  {canEdit && <Button variant="secondary" onClick={onSync} disabled={syncing}>{syncing ? '동기화 중…' : '2.0 사유코드 동기화'}</Button>}
                  <Button variant="secondary" aria-pressed={!!history && history.kind === 'all'}
                    onClick={() => setHistory((h) => (h && h.kind === 'all' ? null : { kind: 'all' }))}>변경이력</Button>
                </>
              )} />
            {status === 'loading' && <div role="status" style={{ color: COLORS.textMuted, fontSize: '12px' }}>불러오는 중…</div>}
            {status === 'forbidden' && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px' }}>이 화면을 볼 권한이 없습니다.</div>}
            {status === 'error' && (
              <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                {loadError}
                <Button variant="secondary" size="sm" onClick={() => load()}>다시 시도</Button>
              </div>
            )}
            {syncResult && (
              <div data-testid="claim-v2-sync-result" style={{ fontSize: '12px', color: COLORS.textSub, marginBottom: '8px' }}>
                2.0 사유코드 — 조회 {syncResult.fetched ?? 0} · 추가 {syncResult.added ?? 0} · 갱신 {syncResult.updated ?? 0} · 없어짐 {syncResult.missing ?? 0}
              </div>
            )}
            {status === 'ready' && (
              <>
                <div data-testid="claim-reason-toolbar" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <PillTabs idBase={ID_BASE} ariaLabel="사고구분" value={shownTab} onChange={changeTab}
                    items={TAB_ITEMS.map((t) => ({ key: t.key, label: t.label, count: counts[t.key] }))} />
                  {!ordering && (
                    <SearchInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="코드 / 사유명 검색" width="220px" />
                  )}
                  <label style={{ ...checkBox, fontSize: '12px' }}>
                    <input type="checkbox" checked={withInactive || !!ordering} disabled={!!ordering}
                      onChange={(e) => setWithInactive(e.target.checked)} />사용 중지 포함
                  </label>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {ordering ? (
                      <>
                        <Button onClick={saveOrder} disabled={orderBusy}>{orderBusy ? '저장 중…' : '순서 저장'}</Button>
                        <Button variant="secondary" onClick={cancelOrdering} disabled={orderBusy}>취소</Button>
                      </>
                    ) : (
                      <>
                        {canEdit && <Button variant="secondary" onClick={startOrdering}><span aria-hidden="true">⇅ </span>순서 변경</Button>}
                        <Button variant="secondary" onClick={onExport} disabled={exporting}>
                          <span aria-hidden="true">⬇ </span>{exporting ? '내려받는 중…' : '엑셀 다운로드'}
                        </Button>
                        {canEdit && <Button onClick={startCreate}><span aria-hidden="true">+ </span>코드 추가</Button>}
                      </>
                    )}
                  </div>
                </div>
                {ordering && (
                  <div data-testid="claim-order-hint" style={{ fontSize: '12px', color: COLORS.textSub, marginBottom: '8px' }}>
                    ▲▼ 로 같은 사고구분 안에서 순서를 바꾼 뒤 [순서 저장]을 누르세요. 사용 중지된 코드도 함께 저장됩니다.
                  </div>
                )}
                {orderError && <div role="alert" style={{ ...errText, marginTop: 0, marginBottom: '8px' }}>{orderError}</div>}
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ ...card, flex: '3 1 560px', minWidth: 0 }}>
                    <h2 style={{ margin: '0 0 10px', fontSize: '14px', fontWeight: 600, color: COLORS.text }}>
                      상세사유 코드 목록 <span style={{ fontSize: '12px', fontWeight: 400, color: COLORS.textMuted }}>— 클레임 등록·고객사 포털·리포트에 공통 사용</span>
                    </h2>
                    <div role="tabpanel" id={`${ID_BASE}-panel-${shownTab}`} aria-labelledby={`${ID_BASE}-tab-${shownTab}`} style={{ overflowX: 'auto' }}>
                      <table data-testid="claim-reason-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            {TABLE_COLUMNS.map((h) => <th key={h} scope="col" style={th}>{h}</th>)}
                            {ordering && <th scope="col" style={th}>순서</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.length === 0 && (
                            <tr><td colSpan={columnCount} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>사유가 없습니다.</td></tr>
                          )}
                          {rows.map((it, i) => {
                            const sameBefore = i > 0 && rows[i - 1].accident_type === it.accident_type;
                            const sameAfter = i < rows.length - 1 && rows[i + 1].accident_type === it.accident_type;
                            let span = 1;
                            if (!sameBefore) {
                              while (i + span < rows.length && rows[i + span].accident_type === it.accident_type) span += 1;
                            }
                            const picked = selected === it.id;
                            const bg = picked ? COLORS.primarySoft : undefined;
                            return (
                              <tr key={it.id} data-testid="claim-reason-row" aria-selected={picked} onClick={() => selectRow(it.id)} style={{ cursor: 'pointer' }}>
                                {!sameBefore && (
                                  <td rowSpan={span} style={{ ...td, background: COLORS.surface }}>
                                    <span style={badge(ACCIDENT_TONE[it.accident_type] || 'gray')}>{ACCIDENT_SHORT[it.accident_type] || it.accident_type}</span>
                                  </td>
                                )}
                                <td style={{ ...td, background: bg, whiteSpace: 'nowrap' }}>
                                  <button type="button" aria-label={`${it.code} ${it.name}`} style={codeBtn}>{it.code}</button>
                                </td>
                                <td style={{ ...td, background: bg }}>{it.name}</td>
                                <td style={{ ...td, background: bg }}>{linkLabel(it, ruleLabel(it.comp_rule_group))}</td>
                                <td style={{ ...td, background: bg, whiteSpace: 'nowrap' }}>{it.comp_scope === 'select' ? '일부·전제품' : DASH}</td>
                                <td style={{ ...td, background: bg }}>{evidenceText(it)}</td>
                                <td style={{ ...td, background: bg }}>
                                  <span style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' }}>
                                    <span style={badge(it.active ? 'success' : 'gray')}>{it.active ? '사용' : '사용 중지'}</span>
                                    {it.needs_check && <NeedsCheck />}
                                  </span>
                                </td>
                                {ordering && (
                                  <td style={{ ...td, background: bg, whiteSpace: 'nowrap' }}>
                                    <Button variant="quiet" size="sm" aria-label={`${it.code} 위로`} disabled={!sameBefore}
                                      onClick={(e) => { e.stopPropagation(); move(it.id, -1); }}>▲</Button>
                                    <Button variant="quiet" size="sm" aria-label={`${it.code} 아래로`} disabled={!sameAfter}
                                      onClick={(e) => { e.stopPropagation(); move(it.id, 1); }}>▼</Button>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p data-testid="claim-reason-note" style={{ margin: '10px 0 0', fontSize: '12px', color: COLORS.textMuted }}>{TABLE_NOTE}</p>
                  </div>
                  <div style={{ ...card, flex: '2 1 380px', minWidth: 0 }}>
                    {selected === 'new' && canEdit && (
                      <ReasonDetail key={`new-${tab}`} item={null} tab={tab} data={data} canEdit={canEdit} runSave={runSave}
                        onCreated={(id) => setSelected(id)} onClose={() => setSelected(null)} onOpenHistory={openCodeHistory} />
                    )}
                    {current && (
                      <ReasonDetail key={`${current.id}-${detailKey}`} item={current} tab={tab} data={data} canEdit={canEdit} runSave={runSave}
                        onCreated={() => {}} onClose={() => setSelected(null)} onOpenHistory={openCodeHistory} />
                    )}
                    {!current && selected !== 'new' && (
                      <div style={{ fontSize: '13px', color: COLORS.textMuted }}>왼쪽 표에서 코드를 고르세요.</div>
                    )}
                  </div>
                </div>
                <ClaimWmsReasonPanel data={data} canEdit={canEdit} runSave={runSave} version={detailKey} />
              </>
            )}
          </Page>
        </div>
        {history && (
          <ClaimHistoryPanel key={`${history.kind}-${history.code || ''}-${historyKey}`}
            load={history.kind === 'code' ? history.load : claimsAPI.reasonsHistory}
            subtitle={history.kind === 'code' ? history.subtitle : null}
            onClose={() => setHistory(null)} />
        )}
      </main>
    </AppShell>
  );
}
